"use client";

import React, { Component, ReactNode } from "react";
import { PageLoadError } from "./error-state";
import {
    getSafeErrorMessage,
    logSafeError,
    type ErrorContext,
    type SafeErrorMessage,
} from "@/lib/safe-error";

interface Props {
    children: ReactNode;
    context?: ErrorContext;
}

interface State {
    hasError: boolean;
    message: SafeErrorMessage | null;
}

/**
 * Top-level boundary for unexpected crashes in page content.
 * Prevents white-screen failures and never exposes raw error text.
 */
export class AppErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, message: null };
    }

    static getDerivedStateFromError(): State {
        // Set a placeholder immediately so render never returns null / blank.
        return {
            hasError: true,
            message: getSafeErrorMessage(
                new Error("Unexpected application error"),
                "generic",
            ),
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        logSafeError(error, "app-error-boundary");
        this.setState({
            message: getSafeErrorMessage(error, this.props.context ?? "generic"),
        });
        console.error("[AppErrorBoundary]", errorInfo.componentStack);
    }

    handleRetry = () => {
        this.setState({ hasError: false, message: null });
    };

    render() {
        if (this.state.hasError) {
            // Never return null here — that produced a blank/black screen between
            // getDerivedStateFromError and componentDidCatch.
            const message =
                this.state.message ??
                getSafeErrorMessage(
                    new Error("Unexpected application error"),
                    this.props.context ?? "generic",
                );
            return (
                <PageLoadError message={message} onRetry={this.handleRetry} />
            );
        }

        return this.props.children;
    }
}
